import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type {
  CompanyPodcastOption,
  MetaIgLinkedAsset,
  MetaMappingRow,
} from "./metaConnectUtils";
import { inviteTextSx } from "./inviteTypography";

export interface MetaAssetMappingProps {
  assets: MetaIgLinkedAsset[];
  selectedPodcasts: CompanyPodcastOption[];
  mappings: MetaMappingRow[];
  onMappingsChange: (mappings: MetaMappingRow[]) => void;
  submitting: boolean;
  onSubmit: () => void;
  onBack: () => void;
}

function igOptionLabel(asset: MetaIgLinkedAsset): string {
  return asset.igUsername
    ? `@${asset.igUsername.replace(/^@+/, "")}`
    : asset.igId;
}

export const MetaAssetMapping: React.FC<MetaAssetMappingProps> = ({
  assets,
  selectedPodcasts,
  mappings,
  onMappingsChange,
  submitting,
  onSubmit,
  onBack,
}) => {
  const updateMapping = (podcastId: string, pageId: string) => {
    const asset = assets.find((a) => a.pageId === pageId);
    onMappingsChange(
      mappings.map((row) =>
        row.podcastId === podcastId
          ? { ...row, pageId, igId: asset?.igId ?? "" }
          : row,
      ),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Typography variant="body2" sx={inviteTextSx}>
        Asocia cada podcast con su cuenta de Instagram.
      </Typography>

      {selectedPodcasts.map((podcast) => {
        const row = mappings.find((m) => m.podcastId === podcast.podcastId);
        return (
          <Box
            key={podcast.podcastId}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                src={podcast.image}
                alt={podcast.title}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.5,
                  fontSize: "0.75rem",
                  bgcolor: "common.black",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {podcast.title.trim().charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" sx={{ ...inviteTextSx, fontWeight: 600 }}>
                {podcast.title}
              </Typography>
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel id={`ig-${podcast.podcastId}`}>Cuenta de Instagram</InputLabel>
              <Select
                labelId={`ig-${podcast.podcastId}`}
                label="Cuenta de Instagram"
                value={row?.pageId ?? ""}
                onChange={(e) => updateMapping(podcast.podcastId, e.target.value)}
                renderValue={(pageId) => {
                  const a = assets.find((x) => x.pageId === pageId);
                  if (!a) return "";
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={a.igProfilePictureUrl}
                        alt={a.igUsername ?? a.pageName}
                        sx={{
                          width: 22,
                          height: 22,
                          fontSize: "0.65rem",
                          bgcolor: "common.black",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {(a.igUsername ?? a.pageName).trim().charAt(0).toUpperCase()}
                      </Avatar>
                      {igOptionLabel(a)}
                    </Box>
                  );
                }}
              >
                {assets.map((asset) => (
                  <MenuItem key={asset.pageId} value={asset.pageId}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        src={asset.igProfilePictureUrl}
                        alt={asset.igUsername ?? asset.pageName}
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.8rem",
                          bgcolor: "common.black",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {(asset.igUsername ?? asset.pageName).trim().charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={igOptionLabel(asset)}
                      secondary={asset.pageName}
                      primaryTypographyProps={{ variant: "body2" }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      })}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitting}
          fullWidth
          sx={{
            backgroundColor: "#1877F2",
            color: "#fff",
            "&:hover": { backgroundColor: "#166FE5" },
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Confirmar vinculación"
          )}
        </Button>
        <Button variant="text" onClick={onBack} disabled={submitting} fullWidth>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default MetaAssetMapping;
